import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useContext } from '@/hooks';
import { Card, CardContent, CardHeader, CardTitle, Button, Spinner } from '@/components/ui';
import { Building2, Plus, ArrowRight, Edit2, Trash2, MoreVertical } from 'lucide-react';
import { Clinic } from '@/types/models';
import { clinicsService } from '@/services/api/clinics.service';
import toast from 'react-hot-toast';
import { CreateClinicModal } from '@/components/clinics/CreateClinicModal';
import { EditClinicModal } from '@/components/clinics/EditClinicModal';
import { DeleteClinicModal } from '@/components/clinics/DeleteClinicModal';

export function ClinicSelection() {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const { setClinic } = useContext();

  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingClinic, setEditingClinic] = useState<Clinic | null>(null);
  const [deletingClinic, setDeletingClinic] = useState<Clinic | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  useEffect(() => {
    loadClinics();
  }, []);

  const loadClinics = async () => {
    setIsLoading(true);
    try {
      let clinics: Clinic[];

      if (isAdmin) {
        // For admins, get all clinics
        const response = await clinicsService.getAll({
          limit: 100
        });
        clinics = response.data;
      } else {
        // For non-admins, use the accessible endpoint
        clinics = await clinicsService.getAccessible();
      }

      setClinics(clinics || []);
    } catch (error) {
      toast.error('Erro ao carregar clínicas');
      console.error('Failed to load clinics:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectClinic = (clinic: Clinic) => {
    // Don't navigate if clicking on the menu button
    if (openMenuId === clinic.id) {
      return;
    }
    setClinic(clinic);
    // Para usuários médicos: ao ir para /medicos, o DoctorSelection detectará
    // automaticamente que é um médico e redirecionará para /calendar com o médico selecionado
    navigate('/medicos');
  };

  const handleCreateClinic = () => {
    setShowCreateModal(true);
  };

  const handleClinicCreated = (newClinic: Clinic) => {
    setClinics([...clinics, newClinic]);
    setShowCreateModal(false);
    toast.success('Clínica criada com sucesso!');
  };

  const handleEditClinic = (clinic: Clinic, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingClinic(clinic);
    setOpenMenuId(null);
  };

  const handleDeleteClinic = (clinic: Clinic, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingClinic(clinic);
    setOpenMenuId(null);
  };

  const handleClinicUpdated = (updatedClinic: Clinic) => {
    setClinics(clinics.map(c => c.id === updatedClinic.id ? updatedClinic : c));
    setEditingClinic(null);
    toast.success('Clínica atualizada com sucesso!');
  };

  const handleClinicDeleted = () => {
    setClinics(clinics.filter(c => c.id !== deletingClinic?.id));
    setDeletingClinic(null);
    loadClinics(); // Reload to ensure consistency
  };

  const toggleMenu = (clinicId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === clinicId ? null : clinicId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" label="Carregando clínicas..." />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Selecione uma Clínica
            </h1>
            <p className="mt-2 text-gray-600">
              Escolha a clínica em que deseja trabalhar
            </p>
          </div>
        </div>
      </div>

      {clinics.length === 0 ? (
        <Card variant="bordered" className="p-12 text-center">
          <div className="flex flex-col items-center">
            <Building2 className="h-16 w-16 text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Nenhuma clínica encontrada
            </h2>
            <p className="text-gray-600 mb-6 max-w-md">
              {isAdmin
                ? 'Nenhuma clínica cadastrada. Crie a primeira clínica para começar.'
                : 'Você ainda não tem acesso a nenhuma clínica. Entre em contato com o administrador.'}
            </p>
            {isAdmin && (
              <Button onClick={handleCreateClinic} size="lg">
                <Plus className="mr-2 h-5 w-5" />
                Criar Primeira Clínica
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clinics.map((clinic) => (
            <Card
              key={clinic.id}
              variant="bordered"
              className="hover:shadow-lg transition-shadow cursor-pointer group relative"
              onClick={() => handleSelectClinic(clinic)}
            >
              {/* Admin Actions Menu */}
              {isAdmin && (
                <div className="absolute top-4 right-4 z-10">
                  <button
                    onClick={(e) => toggleMenu(clinic.id, e)}
                    className="p-1 rounded hover:bg-gray-100 transition-colors"
                  >
                    <MoreVertical className="h-5 w-5 text-gray-500" />
                  </button>

                  {openMenuId === clinic.id && (
                    <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                      <button
                        onClick={(e) => handleEditClinic(clinic, e)}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                      >
                        <Edit2 className="h-4 w-4" />
                        Editar Clínica
                      </button>
                      <button
                        onClick={(e) => handleDeleteClinic(clinic, e)}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                        Excluir Clínica
                      </button>
                    </div>
                  )}
                </div>
              )}

              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="p-3 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                    <Building2 className="h-8 w-8 text-blue-600" />
                  </div>
                  {!isAdmin && (
                    <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <CardTitle className="text-lg mb-2">{clinic.name}</CardTitle>
                {clinic.address && (
                  <p className="text-sm text-gray-600 mb-1">
                    📍 {clinic.address}
                  </p>
                )}
                {clinic.phone && (
                  <p className="text-sm text-gray-600">
                    📞 {clinic.phone}
                  </p>
                )}
                {!clinic.isActive && (
                  <span className="inline-block mt-2 px-2 py-1 text-xs font-medium text-red-600 bg-red-50 rounded">
                    Inativa
                  </span>
                )}
              </CardContent>
            </Card>
          ))}

          {isAdmin && (
            <Card
              variant="bordered"
              className="hover:shadow-lg transition-shadow cursor-pointer group border-dashed border-2 border-gray-300"
              onClick={handleCreateClinic}
            >
              <CardContent className="h-full flex flex-col items-center justify-center py-12">
                <div className="p-3 bg-gray-50 rounded-lg group-hover:bg-gray-100 transition-colors mb-4">
                  <Plus className="h-8 w-8 text-gray-400 group-hover:text-gray-600" />
                </div>
                <p className="text-gray-600 font-medium">
                  Adicionar Nova Clínica
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateClinicModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleClinicCreated}
        />
      )}

      {editingClinic && (
        <EditClinicModal
          clinic={editingClinic}
          isOpen={!!editingClinic}
          onClose={() => setEditingClinic(null)}
          onClinicUpdated={handleClinicUpdated}
        />
      )}

      {deletingClinic && (
        <DeleteClinicModal
          clinic={deletingClinic}
          isOpen={!!deletingClinic}
          onClose={() => setDeletingClinic(null)}
          onClinicDeleted={handleClinicDeleted}
        />
      )}
    </div>
  );
}